/**
 * Supabase client for CAP (Coverage Assessment Portal)
 */
import { createClient, SupabaseClient } from '@supabase/supabase-js';

import { envSwitch } from '@/utils/environmentUtils';

/**
 * SUPABASE_URL
 *
 * The URL of the Supabase project for each environment
 * TODO: local, dev, qa, pr, and prod still point to the DEV project — update when URLs/keys are available.
 * SMS OTP hooks must be configured in each project before switching.
 *
 * These URLs are NOT secrets — they are public project identifiers
 */
const SUPABASE_URL = envSwitch({
  local: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  dev: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  qa: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  pr: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  stage: 'https://ngxdbhvvxledqxykuwpr.supabase.co',
  prod: 'https://evwudmabosoioeqpdbii.supabase.co',
});

/**
 * SUPABASE_ANON_KEY
 *
 * The publishable (anon) key for the Supabase project.
 *
 * This is NOT a secret — it is designed to be public and embedded in client-side
 * code
 * See: https://supabase.com/docs/guides/api/api-keys
 */
const SUPABASE_ANON_KEY = envSwitch({
  local: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  dev: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  qa: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  pr: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  stage: 'sb_publishable_BpK9nqWUcpYkCJUspzlizw_Y2CAHvtY',
  prod: 'sb_publishable_AMg0lYt9L_sZs5dPz9bmMg_GZ0VOHWO',
});

// Singleton — created once and reused to avoid opening multiple connections.
let client: SupabaseClient | null = null;

/**
 * Returns the Supabase client instance, creating it on first call.
 *
 * Auth options:
 *   persistSession    — Store the session in localStorage so it survives page reloads.
 *   autoRefreshToken  — Automatically refresh the JWT before it expires.
 *   detectSessionInUrl — Supabase reads auth tokens from the URL hash on page load.
 *                        Enabled for the email magic link flow which redirects back
 *                        with #access_token in the URL fragment.
 */
export const getSupabaseClient = (): SupabaseClient => {
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }

  return client;
};

/**
 * Resets the client instance. Used in tests to get a fresh client between test cases.
 */
export const resetSupabaseClient = (): void => {
  client = null;
};
